import { Component,
  ElementRef,
  HostListener,
  Input,
  QueryList,
  ViewChild,
  ViewChildren, ChangeDetectorRef } from '@angular/core';
import { CommonService } from '../../../api/services/common.service';
import { AuthService } from '../../../api/services/auth.service';
import { getAccessValue } from '../../../helpers/rbac.helper';
import { environment } from '../../../../environments/environment';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FORM_ID } from '../../../helpers/form-id.enum';
import { CommonModule } from '@angular/common';
import { NoDataViewComponent } from '../no-data-view/no-data-view.component';
import { ModalRendererComponent } from '../../widgets/modal-renderer/modal-renderer.component';

@Component({
  selector: 'app-location-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NoDataViewComponent,
    ModalRendererComponent,
  ],
  templateUrl: './location-card.component.html',
  styleUrl: './location-card.component.css',
})
export class LocationCardComponent {
  endpoint = environment.api_endpoint;
  @Input() fields: any;
  @Input() formGroup!: FormGroup;
  @Input() activeTab: any;
  @Input() isWriteAccess: boolean = true;
  @ViewChild(ModalRendererComponent) modalRendererRef!: ModalRendererComponent;
  isLoading: boolean = false;
  fieldsData: any;
  formID = FORM_ID.LOCATION_LIST;
  currentOpenModalId: any;
  activeModalContext: any;
  editID: any;
  searchInput: any;
  sortValue: any;
  modalTitle: any;
  profileResponse: any;
  openAboveId: string | null = null;
  openDropdownId: string | null = null;
  @ViewChildren('dropdownButton') dropdownButtons!: QueryList<ElementRef>;
  constructor(
    private commonService: CommonService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {}
  ngOnInit() {
    this.loadData(this.formID, {
      sort: '-createdAt',
    });
    this.getProfileDetails();
  }

  getProfileDetails() {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    this.authService.getProfile(token).subscribe({
      next: (data) => {
        this.profileResponse = data?.data;
        const accessVal = getAccessValue(
          this.profileResponse,
          'settings-module',
          'organization',
        );
        if (accessVal === 'read_only' || accessVal === 'no_access') {
          this.isWriteAccess = false;
        }
        this.cdr.detectChanges();
      },
      error: () => {},
    });
  }

  /** Function to get the data by api or static */
  loadData(formId: any, options: { [key: string]: any } = {}) {
    this.isLoading = true;

    this.commonService.loadListWithOptions(formId, options).subscribe({
      next: (data) => {
        this.fieldsData = data.data.records;
        this.isLoading = false;
       this.cdr.detectChanges();},
      error: () => {
        this.fieldsData = [];
        this.isLoading = false;
       this.cdr.detectChanges();},
    });
  }

  /** Function to set the modal title */
  setModalTitle(title: any) {
    this.modalTitle = !this.isWriteAccess ? 'View Location' : title;
  }

  openEditModal(item: any) {
    this.modalRendererRef?.resetForms();
    this.editID = item._id;
    let formID = FORM_ID.LOCATION_EDIT;

    if (formID) {
      this.activeModalContext = {
        nodeId: formID,
        recordID: item._id,
      };
    }
    setTimeout(() => {
      const modalId = `open_modal_${item._id}`;
      const modal = document.getElementById(modalId);
      if (modal) modal.classList.add('show');
    }, 0);
  }

  closeOnBackdropClick(event: MouseEvent, modalId: any) {
    const modal = document.getElementById(modalId);
    if (event.target === modal) {
      this.closeModal(modalId);
    }
  }

  closeModal(modalID: any) {
    const modal = document.getElementById(modalID);
    if (modal) modal.classList.remove('show');
    this.activeModalContext = null;
  }

  openCurrentModal() {
    const modalId = `open_modal_${this.editID}`;
    const modal = document.getElementById(modalId);
    if (modal) modal.classList.add('show');
  }

  /**
   * Reloads the list and closes the modal after a successful save.
   */
  onModalFormSaved(event: any) {
    this.loadData(this.formID, {
      query: this.searchInput,
      sort: this.sortValue || '-createdAt',
    });
    const modalId = `open_modal_${this.editID}`;
    this.closeModal(modalId);
    setTimeout(() => {
      this.modalRendererRef?.resetForms();
    }, 100);
    this.editID = null;
    this.activeModalContext = null;
  }

  closeAllDropdowns() {
    this.openDropdownId = null;
    this.openAboveId = null;
  }

  /**** Toggle dropdown */
  toggleDropdown(id: string, event: MouseEvent) {
    event.stopPropagation();
    if (this.openDropdownId === id) {
      this.closeAllDropdowns();
      return;
    }
    this.openDropdownId = id;
    this.calculateDropdownPosition(id);
  }

  /**** Check if dropdown is open */
  isDropdownOpen(id: string): boolean {
    return this.openDropdownId === id;
  }

  /****  Close dropdown when clicking anywhere outside */
  @HostListener('document:click')
  onOutsideClick() {
    this.closeAllDropdowns();
  }

  /**
   * Determines whether a dropdown should open above or below based on viewport space.
   */
  calculateDropdownPosition(id: string) {
    const button = this.dropdownButtons.find(
      (btn) => btn.nativeElement.dataset.id === id,
    );
    if (button) {
      const rect = button.nativeElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const dropdownHeight = 200;

      if (spaceBelow < dropdownHeight) {
        this.openAboveId = id;
      } else {
        this.openAboveId = null;
      }
    }
  }

  /**
   * Opens the delete modal for the selected bookmark.
   */
  deleteLocation(item: any) {
    this.editID = item._id;
    let formID = FORM_ID.LOCATION_DELETE;

    if (formID) {
      this.activeModalContext = {
        nodeId: formID,
        recordID: item._id,
      };
      setTimeout(() => {
        const modalId = `open_modal_${item._id}`;
        const modal = document.getElementById(modalId);
        if (modal) modal.classList.add('show');
      }, 0);
    }
  }

  shouldOpenAbove(id: string): boolean {
    return this.openAboveId === id;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    const modalId = `open_modal_${this.editID}`;
    this.closeModal(modalId);
  }
}
